import {
  Badge,
  Box,
  Heading,
  IconButton,
  Image,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { FaDownload } from "react-icons/fa";
import Swal from "sweetalert2";
import linelist from "../../media/directoryReport.png";
import file from "../../media/fileTypeImg.png";
import pbi from "../../media/powerBIReport.png";
import staticImg from "../../media/staticReport.png";
import tableau from "../../media/Tableau.png";
import DataCardModal from "../Common/DataCardModal";

const ItemCard = ({ item, isActive, onClick }) => {
  const [clickedData, setClickedData] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleCardClick = (item) => {
    if (item.type === "link") {
      window.open(item.link || item.subTitle, "_blank", "noopener,noreferrer");
    } else if (item.type === "file" || item.type === "Static") {
      handleDownloadClick(item);
    } else {
      setClickedData(item);
      onOpen();
    }
  };

  const handleDownloadClick = async (item) => {
    const result = await Swal.fire({
      title: "Download File?",
      text: `Do you want to download "${item.title}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3182ce",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, download it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      let url = "";
      let fileName = item.title || "download";

      // 🔹 Blob file
      if (item.type?.toLowerCase() !== "static") {
        url = `/api/getBlob?container=${item.container}&blob=${item.blobName}`;
        fileName = item.blobName;
      } else {
        url = item.link;
        const urlParts = url.split("/");
        const lastPart = urlParts[urlParts.length - 1];
        if (lastPart && lastPart.includes(".")) {
          fileName = lastPart.split("?")[0];
        }
        const isFile = /\.[0-9a-z]+$/i.test(fileName);
        if (!isFile) {
          window.open(url, "_blank");
          return;
        }
      }

      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(downloadLink.href), 100);

      Swal.fire({
        title: "Downloading!",
        text: "Your file download has started.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "Unable to download file. The URL may be blocked by CORS or invalid.",
        icon: "error",
      });
    }
  };

  const IMAGE_ASSETS = {
    pbi,
    linelist,
    staticImg,
    tableau,
    file,
  };

  // Helper function to get the correct image source
  const getImageSource = (cardImg) => {
    if (!cardImg) return staticImg;
    if (cardImg.includes("://")) return cardImg;
    return IMAGE_ASSETS[cardImg] || staticImg;
  };
  const imageSrc = getImageSource(item.cardImg);

  // Color scheme based on type
  const typeColors = {
    powerbi: "purple",
    tableau: "orange",
    file: "teal",
    directory: "blue",
    static: "gray",
  };

  const badgeColor = typeColors[item.type] || "gray";

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      cursor="pointer"
      transition="all 0.3s"
      bg={isActive ? "blue.50" : "white"}
      borderColor={isActive ? "blue.400" : "gray.200"}
      boxShadow={isActive ? "lg" : "sm"}
      _hover={{
        transform: "translateY(-4px)",
        boxShadow: "xl",
        borderColor: "blue.300",
      }}
      onClick={() => handleCardClick(item)}
      position="relative"
      maxW="300px"
    >
      {/* Active Indicator */}
      {isActive && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="4px"
          bg="blue.500"
          zIndex={1}
        />
      )}

      {/* Card Image */}
      <Box bg="bgColor" p={2}>
        <Image
          src={imageSrc}
          alt={item.name}
          objectFit="contain"
          height="140px"
          width="100%"
          borderRadius="xl"
        />
      </Box>

      {/* Card Content */}
      <VStack align="stretch" p={4} spacing={2}>
        {/* Type Badge */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Badge
            colorScheme={badgeColor}
            fontSize="xs"
            textTransform="uppercase"
          >
            {item.type === "directory" ? "Folder" : item.type}
          </Badge>
          <Box>
            {item.type === "file" ||
              (item.type === "Static" && (
                <IconButton
                  size="sm"
                  border="none"
                  aria-label="Download files"
                  color="bgColor"
                  icon={<FaDownload />}
                  onClick={(e) => handleDownloadClick(e, item)}
                />
              ))}
          </Box>
        </Box>

        {/* Title */}
        <Heading
          as="h3"
          size="sm"
          color="bgColor"
          fontWeight="bold"
          fontSize="md"
          minHeight="2rem"
        >
          {item.title}
        </Heading>

        {/* Subtitle */}
        {item.subTitle && (
          <Text
            fontSize="xs"
            color="primary"
            fontWeight="semibold"
            noOfLines={1}
          >
            {item.subTitle}
          </Text>
        )}

        {/* Description */}
        <Text fontSize="xs" color="gray.600" noOfLines={2} minHeight="3rem">
          {item.desc}
        </Text>
      </VStack>
      <DataCardModal isOpen={isOpen} onClose={onClose} data={clickedData} />
    </Box>
  );
};

export default ItemCard;
